import { useMemo } from "react";

import { DateRange, Select } from "../../../../ui/index.ts";
import type { Period, PeriodRange } from "../../types.ts";
import { formatWeekLabel, toIsoDate } from "../../../../../utils/period-range.ts";

import styles from "./period-range-control.module.css";

const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export interface PeriodRangeControlProps {
  period: Period;
  range: PeriodRange;
  onChange: (next: PeriodRange) => void;
  disabled?: boolean;
}

export function PeriodRangeControl({
  period,
  range,
  onChange,
  disabled = false,
}: PeriodRangeControlProps) {
  const monthOptions = useMemo(
    () =>
      MONTH_LABELS.map((label, i) => ({
        value: String(i + 1),
        label,
      })),
    [],
  );

  if (period === "MONTHLY" && range.type === "MONTHLY") {
    const year = new Date().getFullYear();

    return (
      <div className={styles.monthRow}>
        <Select
          label="Month"
          placeholder="Select month"
          value={String(range.month)}
          disabled={disabled}
          options={monthOptions}
          onChange={(v) =>
            onChange({
              type: "MONTHLY",
              month: Number(v),
              year,
            })
          }
        />
      </div>
    );
  }

  if (period === "WEEKLY" && range.type === "WEEKLY") {
    return (
      <div className={styles.weekReadOnly}>
        <span className={`sub-heading-md ${styles.weekReadOnlyLabel}`}>
          Current week
        </span>
        <span className={`body-sm ${styles.weekReadOnlyRange}`}>
          {formatWeekLabel(range.weekStart, range.weekEnd)}
        </span>
      </div>
    );
  }

  if (period === "CUSTOM" && range.type === "CUSTOM") {
    const startDate = range.startDate
      ? new Date(`${range.startDate}T12:00:00`)
      : null;
    const endDate = range.endDate ? new Date(`${range.endDate}T12:00:00`) : null;

    return (
      <div className={styles.customWrap}>
        <DateRange
          label="Date range"
          disabled={disabled}
          startDate={startDate}
          endDate={endDate}
          placeholderText="Select Date Range"
          onChange={([start, end]) => {
            onChange({
              type: "CUSTOM",
              startDate: start ? toIsoDate(start) : null,
              endDate: end ? toIsoDate(end) : null,
            });
          }}
        />
      </div>
    );
  }

  return null;
}
